import { useEffect, useMemo, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import CloseIcon from '@mui/icons-material/Close'
import {
  inputSx,
  primaryButtonSx,
  secondaryButtonSx
} from '../styles/authSx'
import {
  SUPPORT_RECIPIENTS,
  SUPPORT_TYPES,
  SupportRecipient,
  SupportType
} from '../config/support'
import { buildSupportEmailBody, UserLike } from '../utils/userSummary'

type Props = {
  open: boolean
  onClose: () => void
  user: UserLike | null
}

type Slot = 'to' | 'cc' | 'off'

const buildMailto = ({
  to,
  cc,
  subject,
  body
}: {
  to: string[]
  cc: string[]
  subject: string
  body: string
}) => {
  const params = new URLSearchParams()
  if (cc.length) params.set('cc', cc.join(','))
  params.set('subject', subject)
  params.set('body', body)
  return `mailto:${to.join(',')}?${params
    .toString()
    .replace(/\+/g, '%20')}`
}

const AskSupportDialog = ({ open, onClose, user }: Props) => {
  const [type, setType] = useState<SupportType>('technical')
  const [slots, setSlots] = useState<Record<string, Slot>>({})
  const [notes, setNotes] = useState('')
  const [appliedTemplate, setAppliedTemplate] = useState('')
  const [extraEmail, setExtraEmail] = useState('')
  const [extraRecipients, setExtraRecipients] = useState<SupportRecipient[]>(
    []
  )

  useEffect(() => {
    if (!open) return
    const cfg =
      SUPPORT_TYPES.find((t) => t.id === type) || SUPPORT_TYPES[0]
    const next: Record<string, Slot> = {}
    SUPPORT_RECIPIENTS.concat(extraRecipients).forEach((r) => {
      if (cfg.defaultTo.includes(r.email)) next[r.email] = 'to'
      else if (cfg.defaultCc.includes(r.email)) next[r.email] = 'cc'
      else next[r.email] = 'off'
    })
    setSlots(next)
    setNotes((prev) =>
      prev === '' || prev === appliedTemplate ? cfg.notesTemplate : prev
    )
    setAppliedTemplate(cfg.notesTemplate)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, type])

  const setSlot = (email: string, slot: Slot) =>
    setSlots((s) => ({ ...s, [email]: slot }))

  const allRecipients = useMemo(
    () => [...SUPPORT_RECIPIENTS, ...extraRecipients],
    [extraRecipients]
  )

  const toEmails = allRecipients
    .filter((r) => slots[r.email] === 'to')
    .map((r) => r.email)
  const ccEmails = allRecipients
    .filter((r) => slots[r.email] === 'cc')
    .map((r) => r.email)

  const cfg = SUPPORT_TYPES.find((t) => t.id === type) || SUPPORT_TYPES[0]
  const subject = user
    ? `[${cfg.subjectTag}] Issue with ${[user.firstName, user.lastName]
        .filter(Boolean)
        .join(' ') || 'user'} — ${user.voxuid}`
    : `[${cfg.subjectTag}]`
  const body = user ? buildSupportEmailBody(user, notes) : ''
  const mailto = buildMailto({ to: toEmails, cc: ccEmails, subject, body })
  const whatsappUrl = user
    ? `https://wa.me/?text=${encodeURIComponent(`*${subject}*\n\n${body}`)}`
    : ''

  const canSend = toEmails.length > 0 && Boolean(user)
  const canSendWhatsApp = Boolean(user)

  const addExtra = () => {
    const trimmed = extraEmail.trim().toLowerCase()
    if (!trimmed || !/^.+@.+\..+$/.test(trimmed)) return
    if (
      [...SUPPORT_RECIPIENTS, ...extraRecipients].some(
        (r) => r.email === trimmed
      )
    )
      return
    setExtraRecipients((r) => [...r, { email: trimmed, label: trimmed }])
    setSlots((s) => ({ ...s, [trimmed]: 'cc' }))
    setExtraEmail('')
  }

  const handleClose = () => {
    setNotes('')
    setExtraEmail('')
    setExtraRecipients([])
    onClose()
  }

  const dialogPaperSx = (t: any) => ({
    borderRadius: { xs: '16px', sm: '20px' },
    backgroundColor: t.palette.mode === 'dark' ? '#0E0E10' : '#FFFFFF',
    backgroundImage: 'none',
    border: `1px solid ${
      t.palette.mode === 'dark' ? '#1F1F22' : '#E5E5EA'
    }`,
    padding: { xs: 0.5, sm: 1 },
    m: { xs: 1.5, sm: 4 },
    width: { xs: 'calc(100% - 24px)', sm: 'auto' }
  })

  const labelSx = (t: any) => ({
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: t.palette.mode === 'dark' ? '#98989D' : '#6E6E73',
    mb: 1
  })

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          letterSpacing: '-0.01em',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        Ask support
        <IconButton
          aria-label="Close"
          size="small"
          onClick={handleClose}
          sx={(t) => ({
            color: t.palette.mode === 'dark' ? '#98989D' : '#6E6E73'
          })}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2.5 } }}>
        <Box
          sx={(t) => ({
            fontSize: '0.875rem',
            color: t.palette.mode === 'dark' ? '#98989D' : '#6E6E73',
            mb: 2.5
          })}
        >
          Compose an escalation email about this user. Will open in your
          email client pre-filled.
        </Box>

        <Box sx={(t) => labelSx(t)}>Type</Box>
        <ToggleButtonGroup
          exclusive
          fullWidth
          value={type}
          onChange={(_, v) => v && setType(v)}
          sx={(t) => {
            const isDark = t.palette.mode === 'dark'
            return {
              mb: 3,
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                borderRadius: '10px',
                border: `1px solid ${isDark ? '#2A2A2E' : '#D1D1D6'}`,
                color: isDark ? '#F5F5F7' : '#0A0F1C',
                py: 1
              },
              '& .MuiToggleButton-root.Mui-selected': {
                backgroundColor: isDark ? '#FFFFFF' : '#0A0F1C',
                color: isDark ? '#0A0F1C' : '#FFFFFF',
                '&:hover': {
                  backgroundColor: isDark ? '#F0F0F0' : '#1A1F2E'
                }
              },
              '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
                marginLeft: 1,
                borderLeft: `1px solid ${isDark ? '#2A2A2E' : '#D1D1D6'}`,
                borderRadius: '10px'
              },
              '& .MuiToggleButtonGroup-grouped:not(:last-of-type)': {
                borderRadius: '10px'
              }
            }
          }}
        >
          {SUPPORT_TYPES.map((t) => (
            <ToggleButton key={t.id} value={t.id}>
              {t.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Box
          sx={(t) => ({
            fontSize: '0.8125rem',
            color: t.palette.mode === 'dark' ? '#98989D' : '#6E6E73',
            mt: -2,
            mb: 3,
            px: 0.5
          })}
        >
          {cfg.description}
        </Box>

        <Box sx={(t) => labelSx(t)}>Recipients</Box>
        <Stack spacing={1} sx={{ mb: 3 }}>
          {allRecipients.map((r) => (
            <Stack
              key={r.email}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={(t) => ({
                py: 1,
                px: 1.5,
                borderRadius: '10px',
                backgroundColor:
                  t.palette.mode === 'dark' ? '#1C1C1E' : '#F5F5F7'
              })}
            >
              <Stack sx={{ minWidth: 0 }}>
                <Box
                  sx={(t) => ({
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: t.palette.mode === 'dark' ? '#F5F5F7' : '#0A0F1C'
                  })}
                >
                  {r.label}
                </Box>
                <Box
                  sx={(t) => ({
                    fontSize: '0.75rem',
                    color: t.palette.mode === 'dark' ? '#98989D' : '#6E6E73'
                  })}
                >
                  {r.email}
                </Box>
              </Stack>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={slots[r.email] || 'off'}
                onChange={(_, v) => {
                  if (!v) return
                  const currentTos = allRecipients.filter(
                    (rr) => slots[rr.email] === 'to'
                  )
                  if (
                    slots[r.email] === 'to' &&
                    v !== 'to' &&
                    currentTos.length === 1
                  ) {
                    return
                  }
                  setSlot(r.email, v as Slot)
                }}
                sx={(t) => {
                  const isDark = t.palette.mode === 'dark'
                  return {
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      px: 1.25,
                      py: 0.5,
                      border: `1px solid ${isDark ? '#2A2A2E' : '#D1D1D6'}`,
                      color: isDark ? '#F5F5F7' : '#0A0F1C'
                    },
                    '& .MuiToggleButton-root.Mui-selected': {
                      backgroundColor: '#00CFF3',
                      color: '#FFFFFF',
                      borderColor: '#00CFF3',
                      '&:hover': { backgroundColor: '#00B8DB' }
                    }
                  }
                }}
              >
                <ToggleButton value="to">To</ToggleButton>
                <ToggleButton value="cc">Cc</ToggleButton>
                <ToggleButton value="off">Off</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          ))}
        </Stack>

        {toEmails.length === 0 && (
          <Box
            sx={{
              fontSize: '0.8125rem',
              fontWeight: 600,
              color: '#FF453A',
              mb: 2,
              px: 0.5
            }}
          >
            At least one recipient must be set as <strong>To</strong>.
          </Box>
        )}

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ mb: 3 }}
        >
          <TextField
            fullWidth
            placeholder="Add another recipient (email)"
            value={extraEmail}
            onChange={(e) => setExtraEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                addExtra()
              }
            }}
            sx={(t) => ({
              ...inputSx(t),
              mb: 0
            })}
          />
          <Button
            onClick={addExtra}
            disabled={
              !extraEmail.trim() ||
              !/^.+@.+\..+$/.test(extraEmail.trim().toLowerCase())
            }
            sx={(t) => ({
              ...secondaryButtonSx(t),
              height: 'auto',
              alignSelf: 'stretch',
              px: 2.5
            })}
          >
            Add
          </Button>
        </Stack>

        <Box sx={(t) => labelSx(t)}>Notes</Box>
        <TextField
          fullWidth
          multiline
          minRows={4}
          placeholder="Describe the issue (what you tried, what's expected, error messages, etc.)…"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={(t) => ({
            ...inputSx(t),
            mb: 2
          })}
        />
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          pb: { xs: 2, sm: 2.5 },
          gap: 1,
          flexWrap: 'wrap'
        }}
      >
        <Button onClick={handleClose} sx={secondaryButtonSx}>
          Cancel
        </Button>
        <Button
          component="a"
          href={canSendWhatsApp ? whatsappUrl : undefined}
          target="_blank"
          rel="noopener noreferrer"
          onClick={canSendWhatsApp ? handleClose : undefined}
          startIcon={<WhatsAppIcon />}
          disabled={!canSendWhatsApp}
          sx={(t) => ({
            ...secondaryButtonSx(t),
            color: '#FFFFFF',
            backgroundColor: '#25D366',
            borderColor: '#25D366',
            '&:hover': {
              backgroundColor: '#1FBE5A',
              borderColor: '#1FBE5A'
            }
          })}
        >
          Open in WhatsApp
        </Button>
        <Button
          component="a"
          href={canSend ? mailto : undefined}
          onClick={canSend ? handleClose : undefined}
          startIcon={<OpenInNewIcon />}
          disabled={!canSend}
          sx={primaryButtonSx}
        >
          Open in email
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AskSupportDialog
